import React, { useEffect, useState } from "react"
import GlobalSubHeading from "../components/Global/GlobalSubHeading"
import GetCSVData from "../components/Global/GlobalCSVDownload"
import { useWalletActions } from "../store/wallets/walletsAction"
import { useSelector } from "react-redux"
import { selectWallet } from "../store/wallets/walletsSlice"
import GlobalTable from "../components/Table/GlobalTable"
import TableFilter from "../components/Table/TableFilter"
import Tabs from "../components/Tabs"
import { ChevronRight } from "lucide-react"
import DateRangePicker from "../components/DateRangePicker"
import StatusView from "../components/StatusView"
import { useLocation, useNavigate } from "react-router-dom"
import { Dayjs } from "dayjs"
import {
    formatXplAmount,
    getTableIndexNumber,
} from "../utils/utils"
import { getTokenRoundIcon } from "../utils/allConstants"
import moment from "moment"
// Map backend activity values to display names
const getTransactionTypeDisplay = (activity: string): string => {
    const typeMap: { [key: string]: string } = {
        'Withdraw': 'Withdraw',
        'Unlocked': 'Unlock',
        'Claimed': 'Claim',
        'Migrate': 'Migrate',
        'Swap': 'Swap',
        'Rewards': 'Rewards',
        'Purchase': 'Purchase',
        'Deposit': 'Deposit',
    };
    return typeMap[activity] || activity || '-';
}
interface Filters {
    limit: number
    page: number
    status?: string | null
    type?: string | null
    fromDate?: string | null
    toDate?: string | null
}
const TransactionHistory = ({
    showViewAll = true,
}: {
    showViewAll?: boolean
}) => {
    const location = useLocation()
    const { state } = location
    const [selectedTab, setSelectedTab] = useState<string>(
        state?.defaultSelectedTab ? state.defaultSelectedTab : "all"
    )
    const [selectedStatusFilter, setSelectedStatusFilter] =
        useState<string>("all")
    const [fromDate, setFromDate] = useState<Dayjs | null>(null)
    const [toDate, setToDate] = useState<Dayjs | null>(null)
    const { getTransactionHistory } = useWalletActions()
    const [isLoading, setIsLoading] = useState(false)
    const [downloadTransaction, setDownloadTransaction] = useState<any>([])
    const [csvLoading, setCSVLoading] = useState(false)
    const [filters, setFilters] = useState<Filters>({
        limit: 10,
        page: 1,
        status: null,
        type: selectedTab === "all" ? null : selectedTab,
        fromDate: null,
        toDate: null,
    })

    useEffect(() => {
        if (!transactionHistory?.transactions?.length) {
            fetchTransactions({ page: 1, limit: 10 })
        }
    }, [])

    const enableReset = () => {
        return (
            selectedStatusFilter !== "all" ||
            filters.fromDate !== null ||
            filters.toDate !== null
        )
    }
    const handleReset = () => {
        fetchTransactions({ page: 1, limit: 10 })
        setFilters({
            limit: 10,
            page: 1,
            status: null,
            type: selectedTab === "all" ? null : selectedTab,
            fromDate: null,
            toDate: null,
        })
        setSelectedStatusFilter("all")
        setFromDate(null)
        setToDate(null)
    }
    const fetchTransactions = async (filters: Filters) => {
        setIsLoading(true)
        await getTransactionHistory(filters)
        setIsLoading(false)
    }

    const { transactionHistory } = useSelector(selectWallet)

    const navigate = useNavigate()
    const headCells = [
        {
            id: "id",
            label: "ID",
            dataClass: "w-[8%]",
        },
        {
            id: "wallet",
            label: "Wallet",
            dataClass: "w-[8%]",
        },
        {
            id: "amount",
            label: "Amount",
            dataClass: "w-[8%]",
        },
        ...(filters.type === "WITHDRAW" || filters.type === null
            ? [
                  {
                      id: "fee",
                      label: "Fee",
                      dataClass: "w-[8%]",
                  },
                  {
                      id: "netAmount",
                      label: "Net Amount",
                      dataClass: "w-[8%]",
                  },
              ]
            : []),
        {
            id: "type",
            label: "Type",
            dataClass: "w-[8%]",
        },
        {
            id: "hash",
            label: "Transaction Hash",
            dataClass: "w-[12%]",
        },
        {
            id: "status",
            label: "Status",
            dataClass: "w-[8%]",
        },
        {
            id: "date",
            label: "Date",
            dataClass: "w-[8%]",
        },
        {
            id: "remarks",
            label: "Remarks",
            dataClass: "w-[8%]",
        },
    ]
    const rows = transactionHistory?.transactions?.map((trx, i) => ({
        id: getTableIndexNumber({
            index: i,
            limit: filters.limit,
            page: filters.page,
        }),
        wallet: (
            <div className="flex gap-2 items-center">
                {getTokenRoundIcon(trx.wallet)}
                {trx.wallet}
            </div>
        ),
        amount: trx.amount ? `${formatXplAmount(trx.amount)} XPL` : "-",
        ...(filters.type === "WITHDRAW" || filters.type === null
            ? {
                  fee: trx.fee ? formatXplAmount(trx.fee) + " XPL" : "-",
                  netAmount: trx.netAmount
                      ? formatXplAmount(trx.netAmount) + " XPL"
                      : "-",
              }
            : {}),
        type: getTransactionTypeDisplay(trx.activity),
        hash: trx.hash ? (
            <div className="pr-2 break-all text-xs">
                {trx.hash}
            </div>
        ) : "-",
        status: <StatusView status={trx.status} />,
        date: trx.date ? trx.date : "-",
        remarks: trx.remarks ? trx.remarks : "-",
    }))

    return (
        <div className="flex flex-col gap-10 p-4 rounded bg-bgSecondary">
            <div className="flex flex-col gap-6 w-full rounded-lg">
                <div className="flex justify-between items-center">
                    <GlobalSubHeading heading={"Transaction History"} />
                    {showViewAll && (
                        <div
                            className="flex gap-1 items-center text-sm font-medium cursor-pointer text-secondary"
                            onClick={() => {
                                navigate("/wallet/transaction-history")
                            }}
                        >
                            <span>View All</span>
                            <ChevronRight className="w-5 h-5" />
                        </div>
                    )}
                </div>
                <Tabs
                    layoutID={"table-tabs"}
                    onFilterChange={(event: string): void => {
                        const newFilters = {
                            ...filters,
                            type: event === "all" ? null : event,
                            fromDate: null,
                            toDate: null,
                            page: 1,
                        }
                        setFilters(newFilters)
                        fetchTransactions(newFilters)
                    }}
                    setSelectedFilter={setSelectedTab}
                    selectedFilter={selectedTab}
                    hoverTextColor={true}
                    filterType={[
                        { label: "All", value: "all" },
                        { label: "Unlock", value: "UNLOCK" },
                        { label: "Claim", value: "CLAIMED" },
                        { label: "Withdraw", value: "WITHDRAW" },
                    ]}
                />
                <div className="flex flex-col gap-4 justify-between w-full lg:flex-row">
                    <div className="flex flex-col gap-4 screen500:flex-row">
                        <TableFilter
                            filters={[
                                {
                                    name: "All",
                                    value: "all",
                                },
                                {
                                    name: "Success",
                                    value: "COMPLETED",
                                },
                                {
                                    name: "Pending",
                                    value: "PENDING",
                                },
                                {
                                    name: "Rejected",
                                    value: "REJECTED",
                                },
                            ]}
                            selectedFilter={selectedStatusFilter}
                            callBack={(status: string) => {
                                const newFilters = {
                                    ...filters,
                                    status: status === "all" ? null : status,
                                    page: 1,
                                }
                                setSelectedStatusFilter(status)
                                setFilters(newFilters)
                                fetchTransactions({ ...newFilters, limit: 10 })
                            }}
                        />
                        <DateRangePicker
                            fromDate={fromDate}
                            toDate={toDate}
                            setFromDate={setFromDate}
                            setToDate={setToDate}
                            onChange={({ from, to }) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    fromDate: from
                                        ? moment(from).format("YYYY-MM-DD")
                                        : null,
                                    toDate: to
                                        ? moment(to).format("YYYY-MM-DD")
                                        : null,
                                }))
                                if (from && to) {
                                    fetchTransactions({
                                        ...filters,
                                        fromDate: from
                                            ? moment(from).format("YYYY-MM-DD")
                                            : null,
                                        toDate: to
                                            ? moment(to).format("YYYY-MM-DD")
                                            : null,
                                    })
                                }
                            }}
                        />
                        <button
                            disabled={!enableReset()}
                            className={` font-medium self-start screen500:self-auto ${
                                !enableReset()
                                    ? "text-gray-500 cursor-not-allowed"
                                    : "text-primaryOrange"
                            }`}
                            onClick={handleReset}
                        >
                            Reset
                        </button>
                    </div>

                    <GetCSVData
                        disabled={csvLoading}
                        loading={csvLoading}
                        transactionData={downloadTransaction}
                        onclick={() => {
                            setCSVLoading(true)
                            getTransactionHistory({
                                ...filters,
                                limit: 99999,
                                page: 1,
                            })
                                .then((res) => {
                                    const data =
                                        res.data.data.transactions.map(
                                            (item: any, i: number) => {
                                                return {
                                                    ID: getTableIndexNumber({
                                                        index: i,
                                                        limit: filters.limit,
                                                        page: filters.page,
                                                    }),
                                                    Wallet: item.wallet,
                                                    Amount: item.amount
                                                        ? `${formatXplAmount(
                                                              item.amount
                                                          )} XPL`
                                                        : "-",
                                                    ...(filters.type ===
                                                        "WITHDRAW" ||
                                                    filters.type === null
                                                        ? {
                                                              Fee: item.fee
                                                                  ? `${formatXplAmount(
                                                                        item.fee
                                                                    )} XPL`
                                                                  : "-",
                                                              "Net Amount":
                                                                  item.netAmount
                                                                      ? `${formatXplAmount(
                                                                            item.netAmount
                                                                        )} XPL`
                                                                      : "-",
                                                          }
                                                        : {}),
                                                    Type: getTransactionTypeDisplay(
                                                        item.activity
                                                    ),
                                                    "Transaction Hash": item.hash
                                                        ? item.hash
                                                        : "-",
                                                    Status: item.status,
                                                    Date: item.date
                                                        ? item.date
                                                        : "-",
                                                    Remarks: item.remarks
                                                        ? item.remarks
                                                        : "-",
                                                }
                                            }
                                        ) || []

                                    setDownloadTransaction(data)
                                })
                                .finally(() => {
                                    setCSVLoading(false) // stop loading whether success/fail
                                })
                        }}
                        onDownloadReady={() => {
                            // Callback function if needed
                        }}
                    />
                </div>
                <div className="flex w-full h-full">
                    <GlobalTable
                        rows={rows}
                        headCells={headCells}
                        isLoading={isLoading}
                        page={filters.page}
                        totalPages={
                            transactionHistory?.paginate.metadata.page.totalPage
                        }
                        handlePageChange={(page) => {
                            setFilters({ ...filters, page })
                            fetchTransactions({
                                ...filters,
                                page,
                                limit: 10,
                            })
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

export default TransactionHistory
